/**
 * @fileoverview Special Piece Definitions - 特殊ピース統一定義
 * 全レイヤーの単一真実源
 *
 * 新しい特殊ピースを追加する場合はこの定義に追加するだけで全システムに自動反映
 */

const SPECIAL_TYPE_BOMB = 'bomb';

/**
 * 特殊ピース統一定義
 * ゲームロジック・レンダー・UI・オーディオの全情報を一元管理
 *
 * 新特殊ピース追加手順:
 * 1. 下記のオブジェクトに新しい定義を追加
 * 2. specialType名を一意に設定
 * 3. 各レイヤーの必要な情報を記述
 */
export const SPECIAL_PIECE_DEFINITIONS = {
  [SPECIAL_TYPE_BOMB]: {
    // === ゲームロジック ===
    specialType: SPECIAL_TYPE_BOMB,
    minMatchCount: 5,
    gameLogic: {
      effectType: 'area',
      effectParams: { radius: 1 },
      scoreMultiplier: 2.0,
      transformsTo: 'bomb2', // 発動後にbomb2に変換
    },

    // === レンダー・エフェクト ===
    render: {
      effectType: 'bomb-simple', // Bomb1用シンプルエフェクト
      effectParams: { radius: 1 },
    },

    // === UI・CSS ===
    ui: {
      className: SPECIAL_TYPE_BOMB,
      cssClasses: ['special', SPECIAL_TYPE_BOMB],
    },
  },

  bomb2: {
    // === 第2段階Bomb（Bomb1から変換、通常マッチでは生成されない） ===
    specialType: 'bomb2',
    // matchCount, minMatchCount, direction を定義しない = 通常生成なし
    gameLogic: {
      effectType: 'area',
      effectParams: { radius: 1 },
      scoreMultiplier: 3.0, // スコアはより高く
      // transformsTo なし = 最終段階
    },

    // === レンダー・エフェクト ===
    render: {
      effectType: SPECIAL_TYPE_BOMB,
      effectParams: { radius: 1 },
    },

    // === UI・CSS ===
    ui: {
      className: 'bomb2',
      cssClasses: ['special', SPECIAL_TYPE_BOMB, 'bomb2'],
    },
  },

  horizontalStripe: {
    // === ゲームロジック ===
    specialType: 'horizontalStripe',
    matchCount: 4,
    direction: 'col', // マッチ方向
    gameLogic: {
      effectType: 'line',
      effectParams: { axis: 'row' }, // 効果方向
      scoreMultiplier: 1.5,
    },

    // === レンダー・エフェクト ===
    render: {
      effectType: 'stripe',
      direction: 'row', // エフェクト方向
    },

    // === UI・CSS ===
    ui: {
      className: 'horizontalStripe',
      cssClasses: ['special', 'horizontalStripe'],
    },
  },

  verticalStripe: {
    // === ゲームロジック ===
    specialType: 'verticalStripe',
    matchCount: 4,
    direction: 'row', // マッチ方向
    gameLogic: {
      effectType: 'line',
      effectParams: { axis: 'col' }, // 効果方向
      scoreMultiplier: 1.5,
    },

    // === レンダー・エフェクト ===
    render: {
      effectType: 'stripe',
      direction: 'col', // エフェクト方向
    },

    // === UI・CSS ===
    ui: {
      className: 'verticalStripe',
      cssClasses: ['special', 'verticalStripe'],
    },
  },

  skillExplosion: {
    // === ゲームロジック ===
    specialType: 'skillExplosion',
    gameLogic: {
      effectType: 'area',
      effectParams: { radius: 2 },
      scoreMultiplier: 3.0,
    },

    // === レンダー・エフェクト ===
    render: {
      effectType: 'skill',
      effectParams: { radius: 2 },
    },

    // === UI・CSS ===
    ui: {
      className: 'skillExplosion',
      cssClasses: ['special', 'skill-explosion'],
    },
  },

  // === Mix専用特殊ピース（通常マッチでは生成されない） ===

  crossStripe: {
    // === Cross Stripe（Stripe × Stripe Mix） ===
    specialType: 'crossStripe',

    // Mix生成条件（matchCount の代わり）
    mixRecipe: {
      type: 'any_stripe_combo',
    },

    gameLogic: {
      effectType: 'cross', // 既存の十字エフェクト
      effectParams: {},
      scoreMultiplier: 3.0,
    },

    render: {
      effectType: 'cross',
      effectParams: { beamDelayMs: 220, resolveDelayMs: 320 },
    },

    ui: {
      className: 'crossStripe',
      cssClasses: ['special', 'cross'],
    },
  },

  horizontalStripe2: {
    // === Horizontal Stripe × Bomb Mix 第1段階 ===
    specialType: 'horizontalStripe2',

    mixRecipe: {
      type: 'stripe_bomb_combo',
      sources: ['horizontalStripe', 'bomb'],
    },

    gameLogic: {
      effectType: 'stripe_bomb',
      effectParams: { stripeAxis: 'row' },
      scoreMultiplier: 4.0,
      transformsTo: 'horizontalStripeCharged',
    },

    render: {
      effectType: 'stripe_bomb',
      direction: 'row',
      effectParams: { thickness: 3 },
    },

    ui: {
      className: 'horizontalStripe2',
      cssClasses: ['special', 'horizontalStripe'],
    },
  },

  verticalStripe2: {
    // === Vertical Stripe × Bomb Mix 第1段階 ===
    specialType: 'verticalStripe2',

    mixRecipe: {
      type: 'stripe_bomb_combo',
      sources: ['verticalStripe', 'bomb'],
    },

    gameLogic: {
      effectType: 'stripe_bomb',
      effectParams: { stripeAxis: 'col' },
      scoreMultiplier: 4.0,
      transformsTo: 'verticalStripeCharged',
    },

    render: {
      effectType: 'stripe_bomb',
      direction: 'col',
      effectParams: { thickness: 3 },
    },

    ui: {
      className: 'verticalStripe2',
      cssClasses: ['special', 'verticalStripe'],
    },
  },

  horizontalStripeCharged: {
    // === Stripe×Bomb 第2段階（横） ===
    specialType: 'horizontalStripeCharged',

    gameLogic: {
      effectType: 'line',
      effectParams: { axis: 'row' },
      scoreMultiplier: 1.5,
    },

    render: {
      effectType: 'stripe',
      direction: 'row',
    },

    ui: {
      className: 'horizontalStripeCharged',
      cssClasses: ['special', 'horizontalStripe', 'horizontalStripeCharged'],
    },
  },

  verticalStripeCharged: {
    // === Stripe×Bomb 第2段階（縦） ===
    specialType: 'verticalStripeCharged',

    gameLogic: {
      effectType: 'line',
      effectParams: { axis: 'col' },
      scoreMultiplier: 1.5,
    },

    render: {
      effectType: 'stripe',
      direction: 'col',
    },

    ui: {
      className: 'verticalStripeCharged',
      cssClasses: ['special', 'verticalStripe', 'verticalStripeCharged'],
    },
  },

  bigBomb: {
    // === Big Bomb（Bomb × Bomb Mix） ===
    specialType: 'bigBomb',

    mixRecipe: {
      type: 'bomb_bomb_combo',
      sources: ['bomb', 'bomb'],
    },

    gameLogic: {
      effectType: 'area', // 既存のエリアエフェクト
      effectParams: { radius: 2 }, // 5×5範囲
      scoreMultiplier: 5.0,
      transformsTo: 'bigBomb2',
    },

    render: {
      effectType: 'big-bomb-stage1',
      effectParams: { radius: 2 },
    },

    ui: {
      className: 'bigBomb',
      cssClasses: ['special', SPECIAL_TYPE_BOMB], // 既存のbomb CSSを再利用
    },
  },

  bigBomb2: {
    // === 第2段階Big Bomb（bigBombから変換、通常マッチでは生成されない） ===
    specialType: 'bigBomb2',
    // matchCount, minMatchCount, direction を定義しない = 通常生成なし

    gameLogic: {
      effectType: 'area',
      effectParams: { radius: 2 }, // bigBombと同じ5×5範囲を維持
      scoreMultiplier: 6.5,
      // transformsTo なし = 最終段階
    },

    render: {
      effectType: 'big-bomb-stage2',
      effectParams: { radius: 2 },
    },

    ui: {
      className: 'bigBomb2',
      cssClasses: ['special', SPECIAL_TYPE_BOMB, 'bigBomb2'],
    },
  },

  // 将来追加予定
  // lightning: {
  //   specialType: 'lightning',
  //   matchCount: 6,
  //   gameLogic: { effectType: 'cross', effectParams: { size: 3 } },
  //   render: { effectType: 'lightning' },
  //   ui: { className: 'lightning', cssClasses: ['special', 'lightning'] }
  // }
};

// レイヤー別アクセサー関数

// ゲームロジック用
export function getGameLogicDefinition(specialType) {
  const def = SPECIAL_PIECE_DEFINITIONS[specialType];
  if (!def) return null;

  return {
    specialType: def.specialType,
    matchCount: def.matchCount,
    minMatchCount: def.minMatchCount,
    direction: def.direction,
    ...def.gameLogic,
  };
}

// レンダー用
export function getRenderDefinition(specialType) {
  const def = SPECIAL_PIECE_DEFINITIONS[specialType];
  if (!def) return null;

  return {
    specialType: def.specialType,
    ...def.render,
  };
}

// UI用
export function getUIDefinition(specialType) {
  const def = SPECIAL_PIECE_DEFINITIONS[specialType];
  if (!def) return null;

  return {
    specialType: def.specialType,
    ...def.ui,
  };
}

// 全定義取得
export function getAllDefinitions() {
  return Object.keys(SPECIAL_PIECE_DEFINITIONS);
}

// 定義存在チェック
export function hasDefinition(specialType) {
  return specialType in SPECIAL_PIECE_DEFINITIONS;
}
